import { motion } from 'framer-motion';
import { Nav } from './Nav';
import { Footer } from './Footer';

export const TermsData = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
      
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center py-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
            Terms of Service
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Gomens is the operator of the website gomens.ai, a web application for generating images of garments worn by Virtual AI Models  and other AI resources. These Terms of Service ("Terms") apply to all users of gomens.ai
          </p>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-10">
          {/* Section 1: Acceptance of Terms */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-600">
             These Terms constitute a legally binding agreement between you and Gomens. By creating an account, accessing, or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms, and any future amendments and additions to these Terms as published from time to time on the Platform. If you are using the Platform on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms, and "you" and "your" will refer to that organization.
            </p>            
          </motion.section>

          {/* Section 2: Service Description */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.3, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. Services Description
            </h2>
            <p className="text-gray-600">
              Gomens provides an AI Virtual Model SaaS platform that allows users to create, customize, and utilize AI-generated virtual models for various purposes ("Services"). The platform also has other AI services. The specific features and functionalities of the Services may vary depending on your subscription plan.
            </p>
          </motion.section>

          {/* Section 3: Account Registration and Security */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.4, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Account Registration and Security
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Account Creation:</strong> To access certain features of the Platform, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </li>
              <li>
                <strong>Account Confidentiality:</strong> You are responsible for maintaining the confidentiality of your account login information (username and password) and for all activities that occur under your account. You agree to notify Gomens immediately of any unauthorized use of your account or any other breach of security. Gomens will not be liable for any loss or damage arising from your failure to comply with this security obligation.
              </li>
              <li>
                <strong>Eligibility:</strong> You must be at least 18 years old to use the Platform. By using the Platform, you represent and warrant that you meet this age requirement.
              </li>
            </ul>
          </motion.section>

          {/* Section 4: Fees and Payment */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.5, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Fees and Payment
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Subscription Plans:</strong> Access to the Services may require payment of subscription fees. Details of our subscription plans, pricing, and payment terms are available on the Platform.
              </li>
              <li>
                <strong>Billing:</strong> You agree to pay all applicable fees and charges for the Services you select. Refer to our <a href="/refund-policy" className="text-indigo-600 hover:text-indigo-500">Refund Policy</a> for details regarding refunds.
              </li>
              <li>
                <strong>Automatic Renewal:</strong> Unless you cancel your subscription, your subscription will automatically renew at the end of each billing cycle, and you authorize us to charge your payment method for the renewal term.
              </li>
              <li>
                <strong>Changes to Fees:</strong> Gomens reserves the right to change its fees at any time, provided that any such changes will not affect active subscriptions until the start of the next billing cycle. We will provide you with prior notice of any fee changes.
              </li>
            </ul>
          </motion.section>

          {/* Section 5: User Content and AI Models */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.6, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. User Content and AI Models
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>User Content:</strong> You are solely responsible for any data, information, text, graphics, photos, profiles, audio, video, items, and links that you submit, post, or display on or through the Platform ("User Content").
              </li>
              <li>
                <strong>Rights to User Content:</strong> By submitting User Content, you grant Gomens a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to use, reproduce, distribute, prepare derivative works of, display, and perform your User Content in connection with the Platform and Gomens' (and its successors' and affiliates') business, including without limitation for promoting and redistributing part or all of the Platform (and derivative works thereof) in any media formats and through any media channels.
              </li>
              <li>
                <strong>AI Model Output:</strong> The AI Virtual Models generated through the Platform are based on algorithms and data. While we strive for accuracy and quality, Gomens does not warrant that the output of the AI models will be error-free, suitable for your specific purpose, or free from unintended biases. You acknowledge that the AI models may occasionally generate content that is not accurate, complete, or reflective of reality.
              </li>
              <li>
                <strong>Ownership of Generated Models:</strong> Subject to your compliance with these Terms, and solely to the extent that the AI Virtual Models generated by the Platform are deemed "works made for hire" under applicable copyright law, Gomens hereby assigns to you all of its right, title, and interest in and to the specific AI Virtual Model output generated by your unique input and parameters within the Platform. However, this assignment does not include any rights to the underlying AI algorithms, software, or technology used to create the models, which remain the sole property of Gomens or its licensors. You agree to use the generated AI Virtual Models in compliance with all applicable laws and regulations and these Terms.
              </li>
            </ul>
          </motion.section>

          {/* Section 6: Prohibited Uses */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.7, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Prohibited Uses
            </h2>
            <p className="text-gray-600">
              You agree not to use the Platform:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                In any way that violates any applicable federal, state, local, or international law or regulation.
              </li>
              <li>
                For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way by exposing them to inappropriate content, asking for personally identifiable information, or otherwise.
              </li>
              <li>
                To transmit, or procure the sending of, any advertising or promotional material without our prior written consent, including any "junk mail," "chain letter," "spam," or any other similar solicitation.
              </li>
              <li>
                To impersonate or attempt to impersonate Gomens, a Gomens employee, another user, or any other person or entity.
              </li>
              <li>
                To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Platform, or which, as determined by us, may harm Gomens or users of the Platform or expose them to liability.
              </li>
              <li>
                To create AI Virtual Models that are defamatory, obscene, pornographic, hateful, or discriminatory, or that promote violence, illegal activities, or harm to any individual or group.
              </li>
              <li>
                To create AI Virtual Models that infringe upon the intellectual property rights, privacy rights, or publicity rights of any third party.
              </li>
              <li>
                To reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code of the Platform or any AI models.
              </li>
            </ul>
          </motion.section>

          {/* Section 7: Intellectual Property Rights */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.8, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Intellectual Property Rights
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Gomens IP:</strong> The Platform and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof) are owned by Gomens, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
              </li>
              <li>
                <strong>Trademarks:</strong> The Gomens name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Gomens or its affiliates or licensors. You must not use such marks without the prior written permission of Gomens.
              </li>
            </ul>
          </motion.section>

          {/* Section 8: Termination */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.9, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Termination
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>By You:</strong> You may terminate your account at any time by following the instructions on the Platform.
              </li>
              <li>
                <strong>By Gomens:</strong> We may terminate or suspend your account and access to the Platform immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms.
              </li>
              <li>
                <strong>Effect of Termination:</strong> Upon termination, your right to use the Platform will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </li>
            </ul>
          </motion.section>

          {/* Section 9: Disclaimers */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.0, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Disclaimers
            </h2>
            <p className="text-gray-600">
              THE PLATFORM AND ALL SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. GOMENS HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.
            </p><br/>
            <p className="text-gray-600">
              GOMENS DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE PLATFORM OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
          </motion.section>

          {/* Section 10: Limitation of Liability */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.1, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Limitation of Liability
            </h2>
            <p className="text-gray-600">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL GOMENS, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE PLATFORM; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE PLATFORM; (III) ANY CONTENT OBTAINED FROM THE PLATFORM; AND (IV) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
            </p><br/>
            <p className="text-gray-600">
              GOMENS' TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING FROM OR RELATING TO THESE TERMS OR YOUR USE OF THE PLATFORM, REGARDLESS OF THE FORM OF ACTION, IS LIMITED TO THE AMOUNT PAID BY YOU TO GOMENS FOR THE SERVICES IN THE SIX (6) MONTHS PRECEDING THE CLAIM.
            </p>
          </motion.section>

          {/* Section 11: Indemnification */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. Indemnification
            </h2>
            <p className="text-gray-600">
              You agree to defend, indemnify, and hold harmless Gomens, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Platform, including, but not limited to, your User Content, your use of the AI Virtual Models, any use of the Platform's content, services, and products other than as expressly authorized in these Terms, or your use of any information obtained from the Platform.
            </p>
          </motion.section>

          {/* Section 12: Governing Law and Jurisdiction */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              12. Governing Law and Jurisdiction
            </h2>
            <p className="text-gray-600">
              These Terms shall be governed and construed in accordance with the laws of the State of Florida, United States, without regard to its conflict of law provisions.
            </p><br/>
            <p className="text-gray-600">
              Any legal suit, action, or proceeding arising out of, or related to, these Terms or the Platform shall be instituted exclusively in the federal or state courts located in Broward County, Florida, United States, although we retain the right to bring any suit, action, or proceeding against you for breach of these Terms in your country of residence or any other relevant country. You waive any and all objections to the exercise of jurisdiction over you by such courts and to venue in such courts.
            </p>            
          </motion.section>     

          {/* Section 13: Dispute Resolution - Arbitration */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              13. Dispute Resolution - Arbitration
            </h2>
            <p className="text-gray-600">
              At Gomens' sole discretion, it may require you to submit any disputes arising from these Terms or use of the Platform, including disputes arising from or concerning their interpretation, violation, invalidity, non-performance, or termination, to final and binding arbitration under the Rules of Arbitration of the American Arbitration Association applying Florida law.
            </p>           
          </motion.section> 

          {/* Section 13: Miscellaneous */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 1.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              14. Miscellaneous
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2 text-gray-600">
              <li>
                <strong>Entire Agreement:</strong> These Terms and our Privacy Policy constitute the sole and entire agreement between you and Gomens regarding the Platform and supersede all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral, regarding the Platform.
              </li>
              <li>
                <strong>Waiver and Severability:</strong> No waiver by Gomens of any term or condition set out in these Terms shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition, and any failure of Gomens to assert a right or provision under these Terms shall not constitute a waiver of such right or provision. If any provision of these Terms is held by a court or other tribunal of competent jurisdiction to be invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of the Terms will continue in full force and effect.
              </li>
              <li>
                <strong>Assignment:</strong> You may not assign or transfer these Terms, by operation of law or otherwise, without Gomens’ prior written consent. Any attempt by you to assign or transfer these Terms, without such consent, will be null and of no effect. Gomens may assign or transfer these Terms, at its sole discretion, without restriction. Subject to the foregoing, these Terms will bind and inure to the benefit of the parties, their successors, and permitted assigns.
              </li>
              <li>
                <strong>Changes to Terms:</strong> We may revise and update these Terms from time to time in our sole discretion. All changes are effective immediately when we post them and apply to all access to and use of the Platform thereafter. Your continued use of the Platform following the posting of revised Terms means that you accept and agree to the changes. You are expected to check this page frequently so you are aware of any changes, as they are binding on you.
              </li>
              <li>
                <strong>Contact Information:</strong> If you have any questions about these Terms or the Platform, please contact us at <a href="mailto:info@gomens.ai" className="text-indigo-600 hover:text-indigo-500">info@gomens.ai</a>
              </li>
            </ul>   
            <p className="text-gray-600 mt-2">Last updated: July 2025</p>       
          </motion.section>     
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 hidden lg:block">
          <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
            <defs>
              <pattern
                id="pattern-circles"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="2"
                  fill="rgba(124, 58, 237, 0.1)"
                />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#pattern-circles)" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 hidden lg:block">
          <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
            <defs>
              <pattern
                id="pattern-circles-2"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="2"
                  fill="rgba(124, 58, 237, 0.1)"
                />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#pattern-circles-2)" />
          </svg>
        </div>        
      </div>
    </div>
  );
};